import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import { title } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function usePermissionsList() {
  // Use toast
  const toast = useToast()

  const refPermissionListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'permission', label: 'Permission Group' },
    { key: 'actions' },
  ]
  const perPage = ref(10)
  const totalPermissions = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const roleFilter = ref(null)
  const planFilter = ref(null)
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refPermissionListTable.value ? refPermissionListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPermissions.value,
    }
  })

  const refetchData = () => {
    refPermissionListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, roleFilter, planFilter, statusFilter], () => {
    refetchData()
  })

  const fetchPermissions = (ctx, callback) => {
    store
      .dispatch('app/fetchPermissions', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        role: roleFilter.value,
        plan: planFilter.value,
        status: statusFilter.value,
      })
      .then(response => {
        const { results, total } = response.data

        callback(results)
        totalPermissions.value = total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching permissions list',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolvePermissionRoleVariant = role => {
    if (role === 'member') return 'primary'
    if (role === 'manager') return 'warning'
    if (role === 'admin') return 'danger'
    return 'primary'
  }

  const resolvePermissionRoleIcon = role => {
    if (role === 'member') return 'PermissionIcon'
    if (role === 'manager') return 'SettingsIcon'
    if (role === 'admin') return 'ServerIcon'
    return 'PermissionIcon'
  }

  const resolvePermissionStatusVariant = status => {
    if (status) return 'success'
    if (!status) return 'secondary'
    return 'secondary'
  }

  const statusOptions = [
    { label: 'Active', value: true },
    { label: 'Inactive', value: false },
  ]

  return {
    fetchPermissions,
    tableColumns,
    perPage,
    currentPage,
    totalPermissions,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refPermissionListTable,

    statusOptions,
    resolvePermissionRoleVariant,
    resolvePermissionRoleIcon,
    resolvePermissionStatusVariant,
    refetchData,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
  }
}
